import React, { useState, useEffect, useRef } from "react";
import { classNames } from "primereact/utils";
import { Route, Routes, useLocation } from "react-router-dom";

import AppTopbar from "./AppTopbar";
import AppFooter from "./AppFooter";
import AppConfig from "./AppConfig";
import AppRightMenu from "./AppRightMenu";
import AppBreadcrumb from "./AppBreadcrumb";
import AppMenu from "./AppMenu";

import UsersCrud from "./pages/Users";
import PeopleCrud from "./pages/People";
import ParameterCrud from "./pages/Parameters";
import MedicineCrud from "./pages/Medicines";
import MissionsCrud from "./pages/Missions";
import MedicinesReport from "./pages/Reports/Medicines";

import Dashboard from "./components/Dashboard";
import FormLayoutDemo from "./components/FormLayoutDemo";
import InputDemo from "./components/InputDemo";
import FloatLabelDemo from "./components/FloatLabelDemo";
import InvalidStateDemo from "./components/InvalidStateDemo";
import ButtonDemo from "./components/ButtonDemo";
import TableDemo from "./components/TableDemo";
import ListDemo from "./components/ListDemo";
import TreeDemo from "./components/TreeDemo";
import PanelDemo from "./components/PanelDemo";
import OverlayDemo from "./components/OverlayDemo";
import MediaDemo from "./components/MediaDemo";
import MenuDemo from "./components/MenuDemo";
import MessagesDemo from "./components/MessagesDemo";
import FileDemo from "./components/FileDemo";
import ChartDemo from "./components/ChartDemo";
import MiscDemo from "./components/MiscDemo";
import Documentation from "./components/Documentation";
import IconsDemo from "./utilities/IconsDemo";
import CrudDemo from "./pages/CrudDemo";
import CalendarDemo from "./pages/CalendarDemo";
import TimelineDemo from "./pages/TimelineDemo";
import Invoice from "./pages/Invoice";
import Help from "./pages/Help";
import EmptyPage from "./pages/EmptyPage";
import BlocksDemo from "./components/BlocksDemo";

import PrimeReact from "primereact/api";
import { Tooltip } from "primereact/tooltip";

import { getUser } from "./services/auth";

import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";
import "./App.scss";

const routes = [
    { role: ["ADMIN", "DOCTOR", "NURSE", "DENTIST", "PHARMACEUTICAL"], parent: "Cadastro", label: "Pessoas", path: "/", element: PeopleCrud },
    { role: ["ADMIN"], parent: "Gestão", label: "Usuários", path: "/usuarios", element: UsersCrud },
    { role: ["ADMIN"], parent: "Gestão", label: "Parâmetros", path: "/parametros", element: ParameterCrud },
    { role: ["ADMIN"], parent: "Gestão", label: "Missões", path: "/missoes", element: MissionsCrud },
    { role: ["ADMIN", "DOCTOR", "NURSE", "DENTIST", "PHARMACEUTICAL"], parent: "Cadastro", label: "Pessoas", path: "/pessoas", element: PeopleCrud },
    {
        role: ["ADMIN", "DOCTOR", "NURSE", "DENTIST", "PHARMACEUTICAL"],
        parent: "Cadastro",
        label: "Medicamentos",
        path: "/medicamentos",
        element: MedicineCrud,
    },
    {
        role: ["ADMIN", "NURSE"],
        parent: "Relatórios",
        label: "Medicamentos",
        path: "/relatorios/medicamentos",
        element: MedicinesReport,
    },
];

const menu = [
    {
        role: ["ADMIN", "DOCTOR", "NURSE", "DENTIST", "PHARMACEUTICAL"],
        label: "Pessoas",
        icon: "pi pi-id-card",
        to: "/pessoas",
    },

    {
        role: ["ADMIN", "NURSE"],
        label: "Medicamentos",
        icon: "pi pi-verified",
        to: "/medicamentos",
    },
    {
        role: ["ADMIN", "NURSE"],
        label: "Relatórios",
        icon: "pi pi-chart-bar",
        items: [{ label: "Medicamentos", icon: "pi pi-file-pdf", to: "/relatorios/medicamentos" }],
    },
    {
        role: ["ADMIN"],
        label: "Gestão",
        icon: "pi pi-sitemap",
        items: [
            { label: "Missões", icon: "pi pi-globe", to: "/missoes" },
            { label: "Usuarios", icon: "pi pi-user", to: "/usuarios" },
            { label: "Parâmetros", icon: "pi pi-exclamation-triangle", to: "/parametros" },
        ],
    },
];

const App = (props: any) => {
    const [rightMenuActive, setRightMenuActive] = useState(false);
    const [configActive, setConfigActive] = useState(false);
    const [overlayMenuActive, setOverlayMenuActive] = useState(false);
    const [ripple, setRipple] = useState(true);

    const [menuMode, setMenuMode] = useState("horizontal");
    const [sidebarStatic, setSidebarStatic] = useState(true);
    const [pinActive, setPinActive] = useState(true);

    const [staticMenuDesktopInactive, setStaticMenuDesktopInactive] = useState(false);
    const [staticMenuMobileActive, setStaticMenuMobileActive] = useState(false);
    const [menuActive, setMenuActive] = useState(true);
    const [searchActive, setSearchActive] = useState(false);
    const [topbarMenuActive, setTopbarMenuActive] = useState(false);
    const [sidebarActive, setSidebarActive] = useState(true);

    const [activeInlineProfile, setActiveInlineProfile] = useState(false);
    const [resetActiveIndex, setResetActiveIndex] = useState<boolean>(false);

    const copyTooltipRef = useRef<any>();
    const location = useLocation();

    PrimeReact.ripple = true;

    let rightMenuClick: any;
    let configClick: any;
    let menuClick: any;
    let searchClick: boolean = false;
    let topbarItemClick: any;

    useEffect(() => {
        copyTooltipRef && copyTooltipRef.current && copyTooltipRef.current.updateTargetEvents();
    }, [location]);

    useEffect(() => {
        setResetActiveIndex(true);
        setMenuActive(false);
    }, [menuMode]);

    const onDocumentClick = () => {
        if (!searchClick && searchActive) {
            onSearchHide();
        }

        if (!topbarItemClick) {
            setTopbarMenuActive(false);
        }

        if (!menuClick) {
            if (isHorizontal() || isSlim()) {
                setMenuActive(false);
                setResetActiveIndex(true);
            }

            if (overlayMenuActive || staticMenuMobileActive) {
                setOverlayMenuActive(false);
                setStaticMenuMobileActive(false);
            }

            hideOverlayMenu();
            unblockBodyScroll();
        }

        if (!rightMenuClick) {
            setRightMenuActive(false);
        }

        if (configActive && !configClick) {
            setConfigActive(false);
        }

        topbarItemClick = false;
        menuClick = false;
        configClick = false;
        rightMenuClick = false;
        searchClick = false;
    };

    const onSearchHide = () => {
        setSearchActive(false);
        searchClick = false;
    };

    const onMenuModeChange = (menuMode: any) => {
        setMenuMode(menuMode);
        setOverlayMenuActive(false);
    };

    const onRightMenuButtonClick = () => {
        rightMenuClick = true;
        setRightMenuActive(true);
    };

    const onRightMenuClick = () => {
        rightMenuClick = true;
    };

    const onRightMenuActiveChange = (active: any) => {
        setRightMenuActive(active);
    };

    const onConfigClick = () => {
        configClick = true;
    };

    const onConfigButtonClick = (event: any) => {
        setConfigActive(prevState => !prevState);
        configClick = true;
        event.preventDefault();
    };

    const onRippleChange = (e: any) => {
        PrimeReact.ripple = e.value;
        setRipple(e.value);
    };

    const onMenuButtonClick = (event: any) => {
        menuClick = true;

        if (isOverlay()) {
            setOverlayMenuActive(prevState => !prevState);
        }

        if (isDesktop()) {
            setStaticMenuDesktopInactive(prevState => !prevState);
        } else {
            setStaticMenuMobileActive(prevState => !prevState);
        }

        event.preventDefault();
    };

    const hideOverlayMenu = () => {
        setOverlayMenuActive(false);
        setStaticMenuMobileActive(false);
    };

    const onTopbarItemClick = (event: any) => {
        topbarItemClick = true;
        setTopbarMenuActive(prevState => !prevState);
        hideOverlayMenu();
        event.preventDefault();
    };

    const onToggleMenu = (event: any) => {
        menuClick = true;

        if (overlayMenuActive) {
            setOverlayMenuActive(false);
        }

        if (sidebarActive) {
            setSidebarStatic(prevState => !prevState);
        }

        event.preventDefault();
    };

    const onSidebarMouseOver = () => {
        if (menuMode === "sidebar" && !sidebarStatic) {
            setSidebarActive(isDesktop());
            setTimeout(() => {
                setPinActive(isDesktop());
            }, 200);
        }
    };

    const onSidebarMouseLeave = () => {
        if (menuMode === "sidebar" && !sidebarStatic) {
            setTimeout(() => {
                setSidebarActive(false);
                setPinActive(false);
            }, 250);
        }
    };

    const onMenuClick = () => {
        menuClick = true;
    };

    const onChangeActiveInlineMenu = (event: any) => {
        setActiveInlineProfile(prevState => !prevState);
        event.preventDefault();
    };

    const onRootMenuItemClick = () => {
        setMenuActive(prevState => !prevState);
    };

    const onMenuItemClick = (event: any) => {
        if (!event.item.items) {
            hideOverlayMenu();
            setResetActiveIndex(true);
        }

        if (!event.item.items && (isHorizontal() || isSlim())) {
            setMenuActive(false);
        }
    };

    const isHorizontal = () => {
        return menuMode === "horizontal";
    };

    const isSlim = () => {
        return menuMode === "slim";
    };

    const isOverlay = () => {
        return menuMode === "overlay";
    };

    const isDesktop = () => {
        return window.innerWidth > 991;
    };

    const onInputClick = () => {
        searchClick = true;
    };

    const breadcrumbClick = () => {
        searchClick = true;
        setSearchActive(true);
    };

    const unblockBodyScroll = () => {
        if (document.body.classList) {
            document.body.classList.remove("blocked-scroll");
        } else {
            document.body.className = document.body.className.replace(new RegExp("(^|\\b)" + "blocked-scroll".split(" ").join("|") + "(\\b|$)", "gi"), " ");
        }
    };

    const layoutClassName = classNames("layout-wrapper", {
        "layout-static": menuMode === "static",
        "layout-overlay": menuMode === "overlay",
        "layout-overlay-active": overlayMenuActive,
        "layout-slim": menuMode === "slim",
        "layout-horizontal": menuMode === "horizontal",
        "layout-active": menuActive,
        "layout-mobile-active": staticMenuMobileActive,
        "layout-sidebar": menuMode === "sidebar",
        "layout-sidebar-static": menuMode === "sidebar" && sidebarStatic,
        "layout-static-inactive": staticMenuDesktopInactive && menuMode === "static",
        "p-ripple-disabled": !ripple,
    });

    return (
        <div className={layoutClassName} onClick={onDocumentClick}>
            <Tooltip ref={copyTooltipRef} target=".block-action-copy" position="bottom" content="Copied to clipboard" event="focus" />

            <div className="layout-main">
                <AppTopbar
                    items={menu.filter(i => i.role.find(ii => ii === getUser()?.role))}
                    menuMode={menuMode}
                    colorScheme={props.colorScheme}
                    menuActive={menuActive}
                    topbarMenuActive={topbarMenuActive}
                    activeInlineProfile={activeInlineProfile}
                    onTopbarItemClick={onTopbarItemClick}
                    onMenuButtonClick={onMenuButtonClick}
                    onSidebarMouseOver={onSidebarMouseOver}
                    onSidebarMouseLeave={onSidebarMouseLeave}
                    onToggleMenu={onToggleMenu}
                    onChangeActiveInlineMenu={onChangeActiveInlineMenu}
                    onMenuClick={onMenuClick}
                    onMenuItemClick={onMenuItemClick}
                    onRootMenuItemClick={onRootMenuItemClick}
                    resetActiveIndex={resetActiveIndex}
                />

                <AppMenu
                    model={menu.filter(i => i.role.find(ii => ii === getUser()?.role))}
                    onRootMenuItemClick={onRootMenuItemClick}
                    onMenuItemClick={onMenuItemClick}
                    onToggleMenu={onToggleMenu}
                    onMenuClick={onMenuClick}
                    menuMode={menuMode}
                    colorScheme={props.colorScheme}
                    menuActive={menuActive}
                    sidebarActive={sidebarActive}
                    sidebarStatic={sidebarStatic}
                    pinActive={pinActive}
                    onSidebarMouseLeave={onSidebarMouseLeave}
                    onSidebarMouseOver={onSidebarMouseOver}
                    activeInlineProfile={activeInlineProfile}
                    onChangeActiveInlineMenu={onChangeActiveInlineMenu}
                    resetActiveIndex={resetActiveIndex}
                />

                <AppBreadcrumb
                    routes={routes}
                    onMenuButtonClick={onMenuButtonClick}
                    menuMode={menuMode}
                    onRightMenuButtonClick={onRightMenuButtonClick}
                    onInputClick={onInputClick}
                    searchActive={searchActive}
                    breadcrumbClick={breadcrumbClick}
                />

                <div className="layout-main-content">
                    <Routes>
                        {routes
                            .filter(i => i.role.find(ii => ii === getUser()?.role))
                            .map(({ path, element: ElementRoute }) => (
                                <Route path={path} element={<ElementRoute />} />
                            ))}

                        <Route path="/documentation" element={<Documentation />} />
                        <Route path="/formlayout" element={<FormLayoutDemo />} />
                        <Route path="/floatlabel" element={<FloatLabelDemo />} />
                        <Route path="/input" element={<InputDemo />} />
                        <Route path="/invalidstate" element={<InvalidStateDemo />} />
                        <Route path="/button" element={<ButtonDemo />} />
                        <Route path="/table" element={<TableDemo />} />
                        <Route path="/list" element={<ListDemo />} />
                        <Route path="/tree" element={<TreeDemo />} />
                        <Route path="/panel" element={<PanelDemo />} />
                        <Route path="/overlay" element={<OverlayDemo />} />
                        <Route path="/menu/*" element={<MenuDemo />} />
                        <Route path="/message" element={<MessagesDemo />} />
                        <Route path="/media" element={<MediaDemo />} />
                        <Route path="/file" element={<FileDemo />} />
                        <Route path="/chart" element={<ChartDemo colorMode={props.colorScheme} location={location} />} />
                        <Route path="/misc" element={<MiscDemo />} />
                        <Route path="/blocks" element={<BlocksDemo />} />
                        <Route path="/icons" element={<IconsDemo />} />
                        <Route path="/crud" element={<CrudDemo />} />
                        <Route path="/calendar" element={<CalendarDemo />} />
                        <Route path="/help" element={<Help />} />
                        <Route path="/invoice" element={<Invoice colorMode={props.colorScheme} location={location} />} />
                        <Route path="/empty" element={<EmptyPage />} />
                        <Route path="/timeline" element={<TimelineDemo />} />
                    </Routes>
                </div>

                <AppFooter colorScheme={props.colorScheme} />
            </div>

            {/* <AppRightMenu rightMenuActive={rightMenuActive} onRightMenuClick={onRightMenuClick} onRightMenuActiveChange={onRightMenuActiveChange} /> */}

            {/* <AppConfig
                configActive={configActive}
                onConfigButtonClick={onConfigButtonClick}
                onConfigClick={onConfigClick}
                menuMode={menuMode}
                changeMenuMode={onMenuModeChange}
                colorScheme={props.colorScheme}
                changeColorScheme={props.onColorSchemeChange}
                theme={props.theme}
                changeTheme={props.onMenuThemeChange}
                componentTheme={props.componentTheme}
                changeComponentTheme={props.onComponentThemeChange}
                ripple={ripple}
                onRippleChange={onRippleChange}
            /> */}
        </div>
    );
};

export default App;
